import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button, Field, PageHeader, Panel, TextInput } from "@/components/primitives";
import { useYard } from "@/lib/use-yard";
import { addSpot, removeSpot, updateSettings } from "@/lib/yard-data";

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "Settings — PortDwellr" },
      { name: "description", content: "Yard details, detention threshold, gates, dock and staging spots, carrier directory and team roles." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AppShell>
      <SettingsPage />
    </AppShell>
  ),
});

const GATES = [
  { name: "Gate A", mode: "QR + SMS", hours: "05:00 – 19:00" },
  { name: "Gate B", mode: "SMS only", hours: "06:00 – 16:00" },
  { name: "Gate C (exit)", mode: "QR", hours: "24h" },
];

const CARRIERS = [
  { name: "Kestrel Logistics", scac: "KSTL", contact: "dispatch@kestrel.co", lanes: 4 },
  { name: "Bayline Drayage", scac: "BYLD", contact: "ops@bayline.com", lanes: 2 },
  { name: "Ridgeway Transport", scac: "RDGW", contact: "yard@ridgeway.us", lanes: 6 },
  { name: "Coastal Container Co.", scac: "CSTC", contact: "team@coastalcc.com", lanes: 3 },
];

const TEAM = [
  { name: "Dana Whitfield", email: "dana@yard4.co", role: "admin" },
  { name: "Marcus Lee", email: "marcus@yard4.co", role: "dispatcher" },
  { name: "Priya Raman", email: "priya@yard4.co", role: "dispatcher" },
  { name: "Sam Okafor", email: "sam@yard4.co", role: "gate agent" },
];

function SettingsPage() {
  const { settings, spots, yardId } = useYard();
  const [draft, setDraft] = useState(settings);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newSpot, setNewSpot] = useState("");
  const [newKind, setNewKind] = useState<"dock" | "staging">("dock");

  // The store resolves the yard asynchronously. Sync the form with the real
  // row once it lands — but never over the top of unsaved edits.
  useEffect(() => {
    if (!dirty) setDraft(settings);
  }, [yardId, settings, dirty]);

  const edit = (patch: Partial<typeof draft>) => {
    setDirty(true);
    setDraft((d) => ({ ...d, ...patch }));
  };

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        lead="Everything the loop reads from: the yard itself, where trucks can check in, where they can be put, who they haul for, and who on your team can act."
      />

      <p className="mb-8">
        <Link
          to="/settings/billing"
          className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel underline decoration-signal decoration-2 underline-offset-4"
        >
          Billing &amp; subscription →
        </Link>
      </p>

      <div className="space-y-10">
        <Panel title="Yard & detention threshold">
          <form
            className="p-5 md:p-7"
            onSubmit={(e) => {
              e.preventDefault();
              void updateSettings(draft).then(() => setDirty(false));
              setSaved(true);
              window.setTimeout(() => setSaved(false), 2500);
            }}
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Field label="Yard name">
                <TextInput value={draft.yardName} onChange={(e) => edit({ yardName: e.target.value })} />
              </Field>
              <Field label="Timezone">
                <TextInput value={draft.timezone} onChange={(e) => edit({ timezone: e.target.value })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Address">
                  <TextInput value={draft.address} onChange={(e) => edit({ address: e.target.value })} />
                </Field>
              </div>
              <Field label="Detention-free time (hours)" hint="Drives the over-threshold counts on the Gate Manifest and Analytics.">
                <TextInput
                  type="number"
                  min={1}
                  max={24}
                  step={0.5}
                  value={draft.freeTimeHours}
                  onChange={(e) => edit({ freeTimeHours: Number(e.target.value) })}
                />
              </Field>
            </div>
            <div className="mt-7 flex items-center gap-4">
              <Button type="submit" disabled={!yardId}>
                Save yard settings
              </Button>
              {!yardId ? (
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">
                  Loading yard…
                </span>
              ) : null}
              {saved ? (
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-signal-deep">Saved</span>
              ) : null}
            </div>
          </form>
        </Panel>

        <Panel title="Gates">
          <ul>
            {GATES.map((g) => (
              <li key={g.name} className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 last:border-b-0 md:px-6">
                <span className="font-display text-[16px] font-semibold">{g.name}</span>
                <span className="font-mono text-[12px] text-ink-soft">check-in: {g.mode}</span>
                <span className="font-mono text-[12px] text-steel-mid">{g.hours}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title={`Dock doors & staging spots · ${spots.length}`}>
          <div className="border-b border-line p-5 md:p-6">
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newSpot.trim()) return;
                addSpot(newSpot.trim().toUpperCase(), newKind);
                setNewSpot("");
              }}
            >
              <div className="w-[160px]">
                <Field label="Label">
                  <TextInput value={newSpot} onChange={(e) => setNewSpot(e.target.value)} placeholder="D9" />
                </Field>
              </div>
              <div className="w-[180px]">
                <Field label="Type">
                  <select
                    value={newKind}
                    onChange={(e) => setNewKind(e.target.value as "dock" | "staging")}
                    className="w-full border border-line-strong bg-paper px-3 py-[10px] font-mono text-[14px] outline-none focus:ring-2 focus:ring-steel/30"
                  >
                    <option value="dock">Dock door</option>
                    <option value="staging">Staging spot</option>
                  </select>
                </Field>
              </div>
              <Button type="submit" variant="ghost">
                Add spot
              </Button>
            </form>
          </div>
          <ul className="grid grid-cols-1 border-l border-line xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {spots.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 border-b border-r border-line px-4 py-3">
                <span className="min-w-0 break-all font-mono text-[13px]">{s.label}</span>
                <button
                  onClick={() => removeSpot(s.id)}
                  className="shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-steel-mid hover:text-destructive"
                  aria-label={`Remove ${s.label}`}
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Carrier directory">
          <ul>
            {CARRIERS.map((c) => (
              <li key={c.scac} className="grid grid-cols-1 gap-1 border-b border-line px-5 py-4 last:border-b-0 md:grid-cols-4 md:items-center md:px-6">
                <span className="font-display text-[16px] font-semibold">{c.name}</span>
                <span className="font-mono text-[12px] text-steel">{c.scac}</span>
                <span className="font-mono text-[12px] text-ink-soft">{c.contact}</span>
                <span className="font-mono text-[12px] text-steel-mid md:text-right">{c.lanes} active lanes</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Team members & roles">
          <ul>
            {TEAM.map((m) => (
              <li key={m.email} className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 last:border-b-0 md:px-6">
                <div>
                  <div className="font-display text-[16px] font-semibold">{m.name}</div>
                  <div className="font-mono text-[12px] text-ink-soft">{m.email}</div>
                </div>
                <span className="border border-line-strong px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-steel">
                  {m.role}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
